/**
 * Strategy Calculations Utility Functions
 *
 * Purpose: Provide calculation helpers for Analytics Dashboard
 * - Percentile calculations for chart reference lines
 * - Beta spread calculations for arbitrage strategies
 * - Chart data formatting for different visualization modes
 *
 * @module utils/strategyCalculations
 */

/**
 * Calculate percentile position of current spread value
 *
 * Uses linear interpolation between percentile bands to determine
 * where the current spread sits in the distribution (0-100%).
 *
 * Algorithm:
 * - If spread < P1: return 0% (extreme low)
 * - If spread > P99: return 100% (extreme high)
 * - Otherwise: interpolate between closest percentile bands
 *
 * Example:
 * - currentSpread = 15.5
 * - percentileBands = { p1: 10, p25: 14, p50: 16, p75: 18, p99: 22 }
 * - Result: ~45% (between P25 and P50)
 *
 * @param {number} currentSpread - Current spread value
 * @param {Object} percentileBands - Percentile band values from API
 * @param {number} percentileBands.p1 - 1st percentile
 * @param {number} percentileBands.p5 - 5th percentile
 * @param {number} percentileBands.p10 - 10th percentile
 * @param {number} percentileBands.p25 - 25th percentile (Q1)
 * @param {number} percentileBands.p50 - 50th percentile (median)
 * @param {number} percentileBands.p75 - 75th percentile (Q3)
 * @param {number} percentileBands.p90 - 90th percentile
 * @param {number} percentileBands.p95 - 95th percentile
 * @param {number} percentileBands.p99 - 99th percentile
 * @returns {number} Percentile value (0-100), or null if invalid input
 */
export const calculatePercentile = (currentSpread, percentileBands) => {
    // Defensive checks
    if (typeof currentSpread !== 'number' || isNaN(currentSpread)) {
        console.warn('calculatePercentile: Invalid currentSpread', currentSpread);
        return null;
    }

    if (!percentileBands || typeof percentileBands !== 'object') {
        console.warn('calculatePercentile: Invalid percentileBands', percentileBands);
        return null;
    }

    // Extract percentile values (handle case variations: p25, P25, etc.)
    const bands = [
        { percentile: 1, value: percentileBands.p1 ?? percentileBands.P1 },
        { percentile: 5, value: percentileBands.p5 ?? percentileBands.P5 },
        { percentile: 10, value: percentileBands.p10 ?? percentileBands.P10 },
        { percentile: 25, value: percentileBands.p25 ?? percentileBands.P25 },
        { percentile: 50, value: percentileBands.p50 ?? percentileBands.P50 },
        { percentile: 75, value: percentileBands.p75 ?? percentileBands.P75 },
        { percentile: 90, value: percentileBands.p90 ?? percentileBands.P90 },
        { percentile: 95, value: percentileBands.p95 ?? percentileBands.P95 },
        { percentile: 99, value: percentileBands.p99 ?? percentileBands.P99 }
    ].filter(band => typeof band.value === 'number' && !isNaN(band.value));

    if (bands.length === 0) {
        console.warn('calculatePercentile: No valid percentile bands found');
        return null;
    }

    // Sort bands by value (ascending)
    bands.sort((a, b) => a.value - b.value);

    // Edge cases: below P1 or above P99
    if (currentSpread <= bands[0].value) {
        return bands[0].percentile;
    }
    if (currentSpread >= bands[bands.length - 1].value) {
        return bands[bands.length - 1].percentile;
    }

    // Linear interpolation between closest bands
    for (let i = 0; i < bands.length - 1; i++) {
        const lowerBand = bands[i];
        const upperBand = bands[i + 1];

        if (currentSpread >= lowerBand.value && currentSpread <= upperBand.value) {
            // Linear interpolation formula:
            // percentile = lowerPercentile + (spread - lowerValue) / (upperValue - lowerValue) * (upperPercentile - lowerPercentile)
            const ratio = (currentSpread - lowerBand.value) / (upperBand.value - lowerBand.value);
            const interpolated = lowerBand.percentile + ratio * (upperBand.percentile - lowerBand.percentile);
            return Math.round(interpolated * 10) / 10; // Round to 1 decimal place
        }
    }

    // Fallback (should never reach here)
    return 50;
};

/**
 * Calculate Beta Spread for arbitrage strategies
 *
 * Formula: BetaSpread = Futures.Bid - (Beta × Spot.Ask)
 *
 * This represents the arbitrage opportunity between futures and spot markets,
 * adjusted by the beta coefficient (correlation factor).
 *
 * Example:
 * - Futures XAUUSD bid: 2500.00
 * - Spot XAUEUR ask: 2480.00
 * - Beta: 0.85
 * - Result: 2500 - (0.85 × 2480) = 392.00
 *
 * @param {Object} futuresTick - Futures market tick data
 * @param {number} futuresTick.bid - Futures bid price
 * @param {number} futuresTick.ask - Futures ask price (optional, for validation)
 * @param {Object} spotTick - Spot market tick data
 * @param {number} spotTick.bid - Spot bid price (optional, for validation)
 * @param {number} spotTick.ask - Spot ask price
 * @param {number} beta - Beta coefficient (correlation factor, typically 0.7-1.0)
 * @returns {number} Beta spread value, or null if invalid input
 */
export const calculateBetaSpread = (futuresTick, spotTick, beta) => {
    // Defensive checks
    if (!futuresTick || typeof futuresTick !== 'object') {
        console.warn('calculateBetaSpread: Invalid futuresTick', futuresTick);
        return null;
    }

    if (!spotTick || typeof spotTick !== 'object') {
        console.warn('calculateBetaSpread: Invalid spotTick', spotTick);
        return null;
    }

    if (typeof beta !== 'number' || isNaN(beta) || beta <= 0) {
        console.warn('calculateBetaSpread: Invalid beta coefficient', beta);
        return null;
    }

    // Extract prices (handle case variations)
    const futuresBid = futuresTick.bid ?? futuresTick.Bid;
    const spotAsk = spotTick.ask ?? spotTick.Ask;

    if (typeof futuresBid !== 'number' || isNaN(futuresBid)) {
        console.warn('calculateBetaSpread: Invalid futures bid', futuresBid);
        return null;
    }

    if (typeof spotAsk !== 'number' || isNaN(spotAsk)) {
        console.warn('calculateBetaSpread: Invalid spot ask', spotAsk);
        return null;
    }

    // Sanity checks
    if (futuresBid <= 0 || spotAsk <= 0) {
        console.warn('calculateBetaSpread: Prices must be positive', { futuresBid, spotAsk });
        return null;
    }

    // Calculate beta spread: Futures.Bid - (Beta × Spot.Ask)
    const betaSpread = futuresBid - (beta * spotAsk);

    return Math.round(betaSpread * 100) / 100; // Round to 2 decimal places
};

/**
 * Format historical chart data for different visualization modes
 *
 * Transforms raw historical bars into chart-ready format with:
 * - Percentile mode: Show spread with P25/P50/P75 reference lines
 * - Z-Score mode: Show normalized Z-score with ±2σ reference lines
 * - Spread mode: Show raw spread values with daily high/low
 * - Session mode: Color-code by trading session (ASIAN/LONDON/NY)
 *
 * @param {Array<Object>} historicalBars - Array of historical data bars from DataFeed API
 * @param {string} historicalBars[].timestamp - ISO 8601 timestamp
 * @param {number} historicalBars[].spread - Raw spread value
 * @param {number} historicalBars[].beta - Beta coefficient (optional)
 * @param {number} historicalBars[].zScore - Z-Score value (optional)
 * @param {string} historicalBars[].session - Trading session (optional)
 * @param {string} mode - Visualization mode: 'percentile', 'zscore', 'spread', 'session'
 * @param {Object} percentileBands - Percentile bands for reference lines (required for percentile mode)
 * @returns {Array<Object>} Chart-ready data array, or empty array if invalid input
 */
export const formatChartData = (historicalBars, mode = 'percentile', percentileBands = null) => {
    // Defensive checks
    if (!Array.isArray(historicalBars)) {
        console.warn('formatChartData: Invalid historicalBars (not an array)', historicalBars);
        return [];
    }

    if (historicalBars.length === 0) {
        return [];
    }

    const validModes = ['percentile', 'zscore', 'spread', 'session'];
    if (!validModes.includes(mode)) {
        console.warn('formatChartData: Invalid mode', mode);
        return [];
    }

    // Filter out invalid bars
    const validBars = historicalBars.filter(bar => {
        if (!bar || typeof bar !== 'object') return false;
        if (!bar.timestamp) return false;
        if (typeof bar.spread !== 'number' || isNaN(bar.spread)) return false;
        return true;
    });

    if (validBars.length === 0) {
        console.warn('formatChartData: No valid bars after filtering');
        return [];
    }

    // Format based on mode
    switch (mode) {
        case 'percentile':
            return validBars.map(bar => ({
                time: new Date(bar.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }),
                timestamp: bar.timestamp,
                value: bar.spread,
                percentile: percentileBands ? calculatePercentile(bar.spread, percentileBands) : null,
                label: `${bar.spread.toFixed(2)} (${percentileBands ? calculatePercentile(bar.spread, percentileBands)?.toFixed(0) : '?'}%)`
            }));

        case 'zscore':
            return validBars.map(bar => ({
                time: new Date(bar.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }),
                timestamp: bar.timestamp,
                value: bar.zScore ?? 0,
                spread: bar.spread,
                label: `Z-Score: ${(bar.zScore ?? 0).toFixed(2)} | Spread: ${bar.spread.toFixed(2)}`
            }));

        case 'spread':
            return validBars.map(bar => ({
                time: new Date(bar.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }),
                timestamp: bar.timestamp,
                value: bar.spread,
                beta: bar.beta,
                label: `Spread: ${bar.spread.toFixed(2)}${bar.beta ? ` | Beta: ${bar.beta.toFixed(2)}` : ''}`
            }));

        case 'session':
            return validBars.map(bar => ({
                time: new Date(bar.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }),
                timestamp: bar.timestamp,
                value: bar.spread,
                session: bar.session ?? 'UNKNOWN',
                sessionColor: getSessionColor(bar.session),
                label: `${bar.spread.toFixed(2)} | ${bar.session ?? 'UNKNOWN'}`
            }));

        default:
            return [];
    }
};

/**
 * Helper: Get color for trading session
 * @private
 */
const getSessionColor = (session) => {
    const sessionColors = {
        'ASIAN': '#f59e0b',    // Orange
        'LONDON': '#3b82f6',   // Blue
        'NY': '#ef4444',       // Red
        'OVERLAP': '#8b5cf6',  // Purple
        'UNKNOWN': '#6b7280'   // Gray
    };
    return sessionColors[session] ?? sessionColors['UNKNOWN'];
};

/**
 * Calculate reference lines for percentile mode
 *
 * @param {Object} percentileBands - Percentile bands from API
 * @returns {Array<Object>} Reference line configurations for Recharts
 */
export const getPercentileReferenceLines = (percentileBands) => {
    if (!percentileBands || typeof percentileBands !== 'object') {
        return [];
    }

    return [
        {
            y: percentileBands.p25 ?? percentileBands.P25,
            label: 'P25',
            stroke: '#22c55e',
            strokeDasharray: '5 5',
            strokeWidth: 1 // Very thin line for sleek professional look
        },
        {
            y: percentileBands.p50 ?? percentileBands.P50,
            label: 'P50',
            stroke: '#f59e0b',
            strokeDasharray: '5 5',
            strokeWidth: 1 // Very thin line for sleek professional look
        },
        {
            y: percentileBands.p75 ?? percentileBands.P75,
            label: 'P75',
            stroke: '#ef4444',
            strokeDasharray: '5 5',
            strokeWidth: 1 // Very thin line for sleek professional look
        }
    ].filter(line => typeof line.y === 'number' && !isNaN(line.y));
};

/**
 * Calculate reference lines for Z-Score mode
 *
 * @returns {Array<Object>} Reference line configurations for Recharts
 */
export const getZScoreReferenceLines = () => {
    return [
        { y: -2, label: '-2σ', stroke: '#22c55e', strokeDasharray: '5 5', strokeWidth: 1 },
        { y: 0, label: 'Mean', stroke: '#f59e0b', strokeDasharray: '3 3', strokeWidth: 1 },
        { y: 2, label: '+2σ', stroke: '#ef4444', strokeDasharray: '5 5', strokeWidth: 1 }
    ];
};
